import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  addDays,
} from "date-fns";
import { Button } from "./Button";
import chevironLeft from "$/cheviron-left.svg";
import chevironRight from "$/cheviron-right.svg";
import { twMerge } from "tailwind-merge";
import { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { DatePicker } from "./DatePicker";
export const HOTEL = "호텔링";
const TODAY = new Date();
const week = ["일", "월", "화", "수", "목", "금", "토"];
interface DayProps {
  today: Date;
  day: Date;
  index: number;
  schedules?: CalendarProps["schedules"];
  direction?: boolean;
}
type DurationType = "first" | "last" | "only" | undefined;
const Day = ({ schedules, today, day, index, direction }: DayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isOtherMonth = useMemo(() => !isSameMonth(day, today), [day, today]);
  const schedule = useMemo(
    () => schedules?.[format(day, "yyyyMMdd")],
    [schedules, day]
  );
  const hasDot = useMemo(
    () => (schedule?.length ?? 0) > 0 && schedule?.every((x) => x !== HOTEL),
    [schedule]
  );
  const hasDuration = useMemo(
    () => schedule?.some((x) => x === HOTEL),
    [schedule]
  );
  const durationType: DurationType = useMemo(() => {
    if (hasDuration && schedules) {
      const isFirst =
        !schedules[format(addDays(day, -1), "yyyyMMdd")]?.includes(HOTEL);
      const isLast =
        !schedules[format(addDays(day, 1), "yyyyMMdd")]?.includes(HOTEL);

      if (isLast && isFirst) return "only";
      if (isFirst) return "first";
      if (isLast) return "last";
    }
    return undefined;
  }, [day, hasDuration, schedules]);

  const memoSchedule = useMemo(() => schedule?.slice(0, 2), [schedule]);

  return (
    <motion.div
      initial={{
        opacity: 0,
        x: direction ? 100 : direction === false ? -100 : 0,
      }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction ? -100 : direction === false ? 100 : 0 }}
      className="relative h-20 border-b border-[#e9e9e9]"
      ref={ref}
      data-duration={hasDuration}
    >
      <p
        className={twMerge(
          "text-sm text-black my-2 mx-auto w-6 h-6 text-center font-medium leading-6",
          isOtherMonth && "!text-[#ccc]",
          index === 6 && "text-[#4F6AD5]",
          index === 0 && "text-[#F24747]"
        )}
      >
        {format(day, "d")}
      </p>
      {hasDot && (
        <i className="bg-blue w-1 h-1 rounded-full absolute top-8 left-1/2 -ml-0.5" />
      )}
      {hasDuration && (
        <i
          className={twMerge(
            "left-0 w-full h-5 absolute top-2.5 z-1 bg-yellow opacity-20",
            durationType === "only" && "rounded-full",
            durationType === "first" && "rounded-s-full",
            durationType === "last" && "rounded-e-full"
          )}
        />
      )}
      {memoSchedule?.map((x) => (
        <p
          className={twMerge(
            "text-xs text-center text-[#666]",
            x === HOTEL && "text-yellow"
          )}
          key={x}
        >
          {x}
        </p>
      ))}
    </motion.div>
  );
};
// const Header = ({
//   value,
//   onChange,
//   disabled,
// }: {
//   today: Date;
//   value: string;
//   disabled?: boolean;
//   onChange?: (value: string) => unknown;
// }) => {
//   return (

//   );
//   //<p className="text-base font-medium">{month}</p>;
// };
const Week = () => {
  return week.map((day) => (
    <span
      className="text-sm content-center h-8 text-center bg-[#f5f5f5] border-y border-[#e9e9e9]"
      key={day}
    >
      {day}
    </span>
  ));
};
interface CalendarProps {
  disabled?: boolean;
  defaultToday?: Date;
  schedules?: { [key: string]: string[] };
  label?: string;
  dayOnClick?: (day: Date) => void;
}
export const Calendar = ({
  defaultToday = TODAY,
  disabled,
  label,
  schedules,
}: CalendarProps) => {
  const direction = useRef<boolean>();
  const [today, setToday] = useState(defaultToday);
  const value = useMemo(() => format(today, "yyyy.MM"), [today]);
  const startDate = startOfWeek(startOfMonth(today));
  const endDate = endOfWeek(endOfMonth(today));
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const handlePrev = useCallback(() => {
    direction.current = false;
    setToday(new Date(today.setMonth(today.getMonth() - 1)));
  }, [today]);
  const handleNext = useCallback(() => {
    direction.current = true;
    console.log(new Date(today.setMonth(today.getMonth() + 1)));
    setToday(new Date(today.setMonth(today.getMonth() + 1)));
  }, [today]);
  const handleChange = useCallback((value: string) => {
    if (!value) return;
    direction.current = undefined;
    setToday(new Date(value.replace(/\./g, "-")));
  }, []);
  return (
    <div className={twMerge("p-4 mt-2 bg-white rounded-lg", label && "mb-4")}>
      <label className="text-sm font-bold">{label}</label>
      <div
        className={twMerge("flex gap-10 items-center m-auto my-4 w-fit", label)}
      >
        <Button
          onClick={handlePrev}
          className={twMerge(disabled && "invisible")}
        >
          <img src={chevironLeft} alt="이전 달력" />
        </Button>
        <DatePicker
          className="p-0 text-base font-medium border-none"
          defaultValue={value}
          onChange={handleChange}
          disabled={disabled}
          showMonthYearPicker
        />
        <Button
          onClick={handleNext}
          className={twMerge(disabled && "invisible")}
        >
          <img src={chevironRight} alt="다음 달력" />
        </Button>
      </div>
      <div className="grid grid-cols-7">
        <Week />
        {days.map((day, index) => (
          <Day
            direction={direction.current}
            schedules={schedules}
            today={today}
            day={day}
            index={index % 7}
            key={day.toString() + index}
          />
        ))}
      </div>
    </div>
  );
};
